/* eslint-disable array-callback-return */
/* eslint-disable indent */
/**
 * request 网络请求工具
 * 更详细的api文档: https://bigfish.alipay.com/doc/api#request
 */
import { extend } from 'umi-request';
import { history } from 'umi';
import sha256 from 'sha256';
import { isEmpty } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { notification } from 'antd';
import IMessage from './IMessage';
import { getProxyRulesPath } from './dxpProxyRules';
import { trackSensors } from './sensors';
import {
  setLocalStorageWithPath,
  getSignInfo,
  getBrandInfo,
  // extractPathFromUrl,
  getPhoneType,
  getSensorsDeviceType,
} from './utils';

const GCPI18n = JSON.parse(window.localStorage.getItem('GCP_i18n') || '{}');

/**
 * 异常处理程序
 */
const errorHandler = async error => {
  const { response = {} } = error;
  if (!isEmpty(error) && isEmpty(response)) {
    return;
  }
  const { status, url } = response;
  if (status === 502) {
    return;
  }
  let errorInfo;
  try {
    errorInfo = await response.json();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('e', e);
  }
  if (errorInfo && errorInfo.code && errorInfo.code.indexOf('PORTAL-SECURITY') >= 0) {
    notification.error({
      message: `${GCPI18n.lb_request_error || 'request error'} ${status}: ${url}`,
      description: errorInfo.message,
    });
    // history.push(`/${constants.LOGIN_PATH}`);
    return;
  }
  if (status === 401) {
    IMessage.error(
      GCPI18n.lb_sign_in_again ||
        "You haven't signed in yet or the sign in has expired. Please sign in again!",
    );
    // 需要重新登录
    window.dito?.goToLogin ? window.dito.goToLogin() : window.clp?.needLogin?.();
    return;
  }

  // 外部系统鉴权
  const errorMessage = errorInfo.error ? errorInfo.error : errorInfo;
  if (url.indexOf('integration/auth') > -1 && status <= 504 && status >= 500) {
    if (errorMessage.code) {
      IMessage.warning(errorMessage.message);
    } else {
      IMessage.error(errorMessage.message);
    }
    history.push('/403');
    return;
  }
  if (url.indexOf('/activation') > -1 && status <= 504 && status >= 500) {
    if (errorMessage.code) {
      IMessage.warning(errorMessage.message);
    } else {
      IMessage.error(errorMessage.message);
    }
    history.push('/404');
    return;
  }

  if (status === 500) {
    if (errorInfo?.message.includes('401') && (window.dito || window.clp)) {
      // 需要重新登录
      window.dito?.goToLogin ? window.dito.goToLogin() : window.clp?.needLogin?.();
      return;
    }
    if (errorMessage.code) {
      IMessage.warning(errorMessage.description ? errorMessage.description : errorMessage.message);
    } else {
      IMessage.error(errorMessage.message);
    }
  }
};

const dataFun = data => {
  if (!data) {
    return {};
  }
  let str = JSON.stringify(data);
  str = str.replace(/[^a-zA-Z\d]/g, '');
  str = str.replace(/null/g, '');
  return str;
};

/**
 * 配置request请求时的默认参数
 */
const request = extend({
  errorHandler, // 默认错误处理
  // credentials: 'include', // 默认请求是否带上cookie
  prefix: '', // constants.REQUEST_PERFIX,
  // requestType: 'json',
  Accept: 'application/json',
  'Content-Type': 'application/json',
  // 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
  method: 'get',
  getResponse: false, // 是否获取源 response, 返回结果将包裹一层
});

request.interceptors.request.use((urlTemp, options) => {
  const requestStartTime = new Date().getTime();
  const url = urlTemp.replace('/promotion-rest-boot', '');
  // const url = urlTemp;
  const { brandKey, brand } = getBrandInfo();
  const currentBrand = brand[brandKey] || {};

  // 提取原始URL的路径部分
  // const path = extractPathFromUrl(url);

  // 根据品牌信息处理请求URL
  let processedUrl = url;

  let defaultConfig = {};
  try {
    defaultConfig = JSON.parse(window.sessionStorage.getItem('x-default-config')) || {};
    if (defaultConfig?.PROXY_PATH_ENABLED) {
      processedUrl = getProxyRulesPath(url);
    }
  } catch {
    //
  }
  if (
    defaultConfig?.PROXY_PATH_ENABLED &&
    currentBrand.API_DOMAIN &&
    !url.includes('defaultConfig') &&
    !url.includes('http')
  ) {
    // 使用品牌的API_DOMAIN替换原始URL的域名部分
    // processedUrl = `https://api.uat1.test.aws.singtel.com${processedUrl}`;
    processedUrl = `${currentBrand.API_DOMAIN}${processedUrl}`;
  }

  const initAppStr = window.localStorage.getItem('initApp');
  const initApp = JSON.parse(initAppStr) || {};
  const urlParams = history.location.query || {};
  let authtoken = options.headers.Token || initApp.token || urlParams.token || '';
  const method = options.method.toUpperCase();
  const { product, userDimension, token } = history.location.query || {};
  options.headers.timestamp = new Date().getTime();

  let sgAssessToken = {};
  const sgToken = sessionStorage.getItem('sg-access-token');
  if (sgToken) {
    sgAssessToken = {
      Authorization: `Bearer ${sgToken}`,
    };
  }

  let appInfo = {};
  if (window.clp) {
    appInfo = JSON.parse(window.clp?.getLoginInfo?.() || '{}');
  }
  const { token: appToken, deviceModel, version, osVersion, language, clientKey } = appInfo;
  let xClientKey = clientKey;
  if (!xClientKey) {
    xClientKey = 'GCP';
    // xClientKey = 'cx';
    // if (brandKey === 'HEYA') {
    //   xClientKey = 'HEYA_APPS';
    // }
  }

  let headers = {
    ...options.headers,
    product: product || initApp.product || '0',
    userDimension: userDimension || initApp.userDimension || '1',
    ...sgAssessToken,
    Token: authtoken || appToken || '',
    'Device-Type': getPhoneType(),
    'Device-Id': appInfo?.deviceId,
    'Device-Model': deviceModel || '',
    Os: getSensorsDeviceType('lower'),
    'Os-Version': osVersion || '',
    'App-Version': version || '',
    'Accept-Language': language || '',
    'X-Client-Key': xClientKey || '',
    'X-App-Request-Time': requestStartTime,
  };
  if ((product || initApp.product) === 'ESHOP') {
    headers = {
      ...headers,
      eshoptoken: token || initApp.eshoptoken,
    };
  }
  // 假token
  if (options?.data?.paramCode === 'LUCKY_DRAW_ESHOP_WEB_LOGIN_URL') {
    authtoken = uuidv4();
  }
  // 取正常token，如果token当前没有，就随机生成一个假token，此处是为了过查询配置项的校验
  if (
    processedUrl.includes('getConfig') ||
    processedUrl.includes('qryConfigItemParamValueByCode')
  ) {
    authtoken = authtoken || uuidv4();
  }
  const newUrl = processedUrl.replace('/promotion-rest-boot', '');
  if (method === 'GET') {
    headers.signcode = sha256(`${newUrl}${authtoken}32BytesString`);
  } else {
    headers.signcode = sha256(`${newUrl}${dataFun(options.data)}${authtoken}32BytesString`);
  }

  return {
    url: processedUrl,
    options: {
      ...options,
      timeout: 60000,
      headers:
        process.env.UMI_APP_SIGNCODE_METHIOD !== 'gcp' ||
        url.includes('page-builder/v1/pages') ||
        url.includes('theme-management/v1/themes/latest')
          ? {
              ...getSignInfo({ ...options, url: processedUrl, uuidToken: authtoken }),
              'X-App-Request-Time': requestStartTime,
              ...(url.includes('/dxp/loyalty/v1/utm/') && {
                shortLink: window.location.origin + window.location.pathname,
              }),
            }
          : {
              ...headers,
              authtoken,
              'AUTH-TOKEN': authtoken,
              ...(url.includes('/dxp/loyalty/v1/utm/') && {
                shortLink: window.location.origin + window.location.pathname,
              }),
            },
    },
  };
});

function safeGetPathname(url) {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return '';
  }
}

request.interceptors.response.use((response, options) => {
  const requestEndTime = Date.now(); // 毫秒级时间戳

  // 原有的response处理逻辑
  const authToken = response.headers.get('Auth-Token');
  const eshoptoken = response.headers.get('eshoptoken');
  const authtoken = response.headers.get('authtoken');
  const operatorAccNbr = response.headers.get('operatorAccNbr');
  const initAppStr = window.localStorage.getItem('initApp');
  const initApp = JSON.parse(initAppStr) || {};
  const urlParams = history.location.query || {};
  const product = initApp.product || urlParams.product;
  if (response.status === 401 && (product === 'ECARE' || product === '0' || product === '1')) {
    window?.clp?.gotoLoginWhenTokenExpire();
  }

  let initAppTemp = { ...initApp, operatorAccNbr: operatorAccNbr || initApp.operatorAccNbr };
  if (authToken) {
    setLocalStorageWithPath('Auth-Token', authToken);
  }
  if (eshoptoken) {
    initAppTemp = {
      ...initApp,
      eshoptoken,
    };
    window.localStorage.setItem('initApp', JSON.stringify(initAppTemp));
  }
  if (authtoken) {
    initAppTemp = {
      ...initApp,
      token: authtoken,
      operatorAccNbr,
    };
  }
  if (authtoken || operatorAccNbr) {
    window.localStorage.setItem('initApp', JSON.stringify(initAppTemp));
  }

  // 异步处理数据上报，不影响正常业务响应
  setTimeout(() => {
    try {
      // 获取请求相关的信息
      const requestUrl = response.url;
      const requestStartTime = options?.headers?.['X-App-Request-Time'];

      // 计算请求时长
      let eventDuration = 0;
      if (requestStartTime) {
        const duration = (requestEndTime - requestStartTime) / 1000; // 转换为秒
        eventDuration = parseFloat(duration.toFixed(2)); // 保留两位小数
      }

      // const optionsTmp = {
      //   ...options,
      //   url: options.method.toUpperCase() === 'GET' ? requestUrl : options.url,
      // };
      // console.log('requestUrl', requestUrl, eventDuration, getRequestParams(optionsTmp));

      // 准备上报参数
      const trackParams = {
        // 取请求头中的Os
        $screen_name: options.headers?.Os || options.headers?.os || options.headers?.OS || '',

        // 用户访问的完整页面地址
        $url: window.location.href || '',

        // 仅路径部分
        $url_path: window.location.pathname || '',

        // 接口中包含'dxp/game'传Game，其他为Loyalty
        $title: requestUrl.includes('/dxp/game') ? 'Game' : 'Loyalty',

        // 后台API完整调用地址
        serviceUrl: requestUrl,

        // 后台API纯路径（不含域名/IP/参数）
        servicePath: safeGetPathname(requestUrl),

        // 网络返回码
        httpCode: response.status,

        // 请求时长
        event_duration: eventDuration,

        // 请求是否成功
        isSuccess: response.ok && response.status >= 200 && response.status < 300,

        // 错误码和失败原因（先初始化为空，后面从响应中提取）
        errorCode: '',
        failReason: '',

        // 品牌
        busiBrand: initApp?.busiBrand || null,
      };

      // 如果请求失败，获取响应数据
      if (!trackParams.isSuccess) {
        // 克隆response以便读取body，不影响原始response
        const clonedResponse = response.clone();
        clonedResponse
          .json()
          .then(data => {
            trackParams.errorCode = data.retCode || data.code || data.errorCode || '';
            trackParams.failReason = data.retDesc || data.msg || data.message || data.error || '';

            // 上报数据
            trackSensors('ServiceCallInfoCollection', trackParams);
          })
          .catch(() => {
            // 如果解析失败，仍然上报已有数据
            trackSensors('ServiceCallInfoCollection', trackParams);
          });
      } else {
        // 请求成功，直接上报
        trackSensors('ServiceCallInfoCollection', trackParams);
      }
    } catch (error) {
      // 上报异常不影响正常业务
      // console.error('数据上报失败:', error);
    }
  }, 0);

  return response;
});

export default request;
